import { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import AvatarCardStrategy from "@/components/avatar/AvatarCardStrategy";
import { publicAvatarDescription } from "@/lib/avatarUi";
import { ListSkeleton } from "@/components/shared/StateViews";
import { UserRound, Sparkles, ArrowRight } from "lucide-react";

// Universal avatar gate picker (Story 6.1 AC3). Always shown when starting a new
// conversation. S0 (no avatar) → block + create CTA; S1 (one) → preselected,
// confirm in one tap; S2 (many) → compact AvatarCardStrategy grid. Drafts are
// never listed (prereq.avatars only contains complete avatars).
export default function AvatarPickerSheet({
  open, onOpenChange, prereq, loading, onSelect, onCreateAvatar, onCreatePersona,
}) {
  const avatars = prereq?.avatars || [];
  const single = avatars.length === 1;
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (open) setSelected(single ? avatars[0]?.id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, single, prereq]);

  const noPersona = prereq?.requires_brand_persona && !prereq?.user_has_brand_persona;
  const noAvatar = avatars.length === 0 || prereq?.user_has_avatar === false;
  const chosen = avatars.find((a) => a.id === selected);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="avatar-picker-sheet"
        className="bg-brand-surface border-brand-line rounded-t-3xl max-h-[88vh] overflow-y-auto overflow-x-hidden scrollbar-soft"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl text-brand-ink">
            Para quem criar?
          </SheetTitle>
          <SheetDescription className="text-brand-ink2">
            Toda copy é criada para um público. Escolha o público antes de começar.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5">
          {loading || !prereq ? (
            <ListSkeleton count={3} />
          ) : noPersona ? (
            <BlockCta
              icon={Sparkles}
              testid="picker-need-persona"
              title="Configure sua marca primeiro"
              description="A Brand Persona alimenta todos os agentes. Leva poucos minutos por conversa."
              actionLabel="Configurar Brand Persona"
              onAction={onCreatePersona}
            />
          ) : noAvatar ? (
            <BlockCta
              icon={UserRound}
              testid="picker-need-avatar"
              title="Crie seu primeiro avatar"
              description="A ValiosIA precisa saber PARA QUEM você cria. Vamos montar sua cliente ideal por voz."
              actionLabel="Criar meu primeiro avatar"
              onAction={onCreateAvatar}
            />
          ) : (
            <>
              <div className="space-y-2.5 mb-5" data-testid="picker-avatar-list">
                {avatars.map((a) => (
                  <AvatarCardStrategy
                    key={a.id}
                    avatar={a}
                    peerAvatars={avatars}
                    compact
                    selected={selected === a.id}
                    onSelect={() => setSelected(a.id)}
                  />
                ))}
              </div>
              <button
                type="button"
                data-testid="picker-confirm"
                disabled={!chosen}
                onClick={() => chosen && onSelect(chosen)}
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-6 py-3.5 rounded-full font-ui text-[14px] tracking-wide transition-colors min-h-[44px] disabled:opacity-50"
              >
                {chosen ? `Criar para: ${publicAvatarDescription(chosen, avatars)}` : "Escolha um público"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function BlockCta({ icon: Icon, title, description, actionLabel, onAction, testid }) {
  return (
    <div className="text-center py-6" data-testid={testid}>
      <div className="w-16 h-16 rounded-full bg-brand-rose/10 border border-brand-rose/40 flex items-center justify-center mx-auto mb-5">
        <Icon className="w-7 h-7 text-brand-rose" />
      </div>
      <h3 className="font-display text-2xl text-brand-ink mb-2">{title}</h3>
      <p className="text-[14px] text-brand-ink2 max-w-sm mx-auto mb-6">{description}</p>
      <button
        type="button"
        data-testid={`${testid}-action`}
        onClick={onAction}
        className="inline-flex items-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-6 py-3.5 rounded-full font-ui text-[14px] transition-colors min-h-[44px]"
      >
        {actionLabel}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
